import { Head, router } from '@inertiajs/react';
import { ChevronsDownUp, ChevronsUpDown, Crown, Trophy } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { JudgeBallotProject, JudgesStagesFinal } from '@/types';
import { flushAll } from '../components/autosave';
import { BallotHeader } from '../components/BallotHeader';
import { FinalRow, FinalTableHeader } from '../components/FinalRow';
import { PlatanusLogo } from '../components/PlatanusLogo';
import { ProjectSheet } from '../components/ProjectSheet';
import {
  ranks,
  type Standings,
  standings,
  trackOf,
  weightedScore,
} from '../components/scoring';

type SortBy = 'order' | 'score';
type Grouping = 'all' | 'track';

// /judges/stages/:stage_slug/final — the final check (Judges::StagesController#final).
// Every project is graded; the judge sees them ranked overall and per track, with the
// leaders marked, can still fix any grade, and finalises with a confirmation that
// shows who they are crowning. Once finalised it is the read-only final verdict.
export default function Final({
  title,
  eventName,
  judgeName,
  ballot,
}: JudgesStagesFinal) {
  const [sortBy, setSortBy] = useState<SortBy>('score');
  const [grouping, setGrouping] = useState<Grouping>('all');
  const [detailId, setDetailId] = useState<string | null>(null);
  // Rows start collapsed — one line each, grades editable in place, to compare at a
  // glance — and open to show the judge's internal note.
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [confirming, setConfirming] = useState(false);
  const [processing, setProcessing] = useState(false);

  const completed = ballot.completedAt !== null;
  const { criteria, projects } = ballot;
  const detail = projects.find((p) => p.id === detailId) ?? null;
  const results = standings(projects, criteria);
  const overallIds = new Set(results.overall.map((p) => p.id));
  const trackWinnerIds = new Set(
    results.byTrack.flatMap(([, leaders]) => leaders.map((p) => p.id)),
  );

  const scoreOf = (project: JudgeBallotProject) =>
    weightedScore(project.scores, criteria) ?? -1;
  const sorted = (list: JudgeBallotProject[]) =>
    [...list].sort((a, b) =>
      sortBy === 'score'
        ? scoreOf(b) - scoreOf(a) || a.order - b.order
        : a.order - b.order,
    );
  const groups: [string | null, JudgeBallotProject[]][] =
    grouping === 'all'
      ? [[null, sorted(projects)]]
      : results.byTrack.map(([track]) => [
          track,
          sorted(projects.filter((p) => trackOf(p) === track)),
        ]);

  const allExpanded = expandedIds.size === projects.length;
  const toggle = (id: string) =>
    setExpandedIds((current) => {
      const next = new Set(current);
      if (!next.delete(id)) next.add(id);
      return next;
    });

  const finalize = () => {
    setProcessing(true);
    router.post(
      `/judges/stages/${ballot.stageSlug}/complete`,
      {},
      {
        preserveScroll: true,
        onSuccess: () => toast.success('Evaluación finalizada'),
        onError: (errors) =>
          toast.error(
            (errors as unknown as Record<string, string>).base ??
              'No se pudo finalizar la evaluación',
          ),
        onFinish: () => {
          setProcessing(false);
          setConfirming(false);
        },
      },
    );
  };

  return (
    <>
      <Head title={title} />
      <div className="theme-ph26 selection-on-light min-h-screen bg-background font-mono text-foreground">
        <div className="mx-auto flex w-full max-w-5xl flex-col gap-8 px-4 py-8 sm:py-12">
          <BallotHeader
            ballot={ballot}
            judgeName={judgeName}
            eventName={eventName}
            back={{
              href: `/judges/stages/${ballot.stageSlug}`,
              label: 'Volver al stage',
            }}
            kicker="Final check"
            summary="Nota ponderada de 0 a 5"
          />

          <Podium results={results} criteria={criteria} />

          <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
            <Toggle
              label="Ordenar"
              value={sortBy}
              onChange={setSortBy}
              options={[
                { value: 'score', label: 'Por nota' },
                { value: 'order', label: 'Orden del stage' },
              ]}
            />
            <Toggle
              label="Ver"
              value={grouping}
              onChange={setGrouping}
              options={[
                { value: 'all', label: 'Todos' },
                { value: 'track', label: 'Por track' },
              ]}
            />
            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                setExpandedIds(
                  allExpanded ? new Set() : new Set(projects.map((p) => p.id)),
                )
              }
            >
              {allExpanded ? (
                <ChevronsDownUp className="mr-1 h-4 w-4" />
              ) : (
                <ChevronsUpDown className="mr-1 h-4 w-4" />
              )}
              {allExpanded ? 'Colapsar todo' : 'Expandir todo'}
            </Button>
          </div>

          <div className="flex flex-col gap-6">
            {groups.map(([track, list]) => {
              const placeOf = ranks(list, criteria);
              const bestScores = Object.fromEntries(
                criteria.map((criterion) => [
                  criterion.id,
                  Math.max(
                    ...list.map((p) => p.scores[criterion.id] ?? -1),
                  ),
                ]),
              );
              return (
                <section
                  key={track ?? 'all'}
                  className="flex flex-col gap-2"
                >
                  {track && (
                    <h2 className="border-primary border-b-2 pb-1 font-bold text-primary text-xl">
                      {track}{' '}
                      <span className="font-normal text-primary/60 text-sm">
                        ({list.length})
                      </span>
                    </h2>
                  )}
                  <FinalTableHeader criteria={criteria} />
                  {list.map((project) => (
                    <FinalRow
                      key={project.id}
                      stageSlug={ballot.stageSlug}
                      project={project}
                      criteria={criteria}
                      rank={placeOf.get(project.id) ?? null}
                      bestScores={bestScores}
                      overallWinner={overallIds.has(project.id)}
                      trackWinner={trackWinnerIds.has(project.id)}
                      highlighted={
                        overallIds.has(project.id) ||
                        (grouping === 'track' &&
                          trackWinnerIds.has(project.id))
                      }
                      readOnly={completed}
                      expanded={expandedIds.has(project.id)}
                      onToggle={() => toggle(project.id)}
                      onExpand={() => setDetailId(project.id)}
                    />
                  ))}
                </section>
              );
            })}
          </div>

          {!completed && (
            <div className="flex flex-col gap-3 border-2 border-primary border-dashed p-5 sm:flex-row sm:items-center sm:justify-between">
              <div className="text-sm">
                <p className="font-bold text-primary">Finalizar evaluación</p>
                <p className="text-primary/70">
                  Confirma tus ganadores. Después de finalizar ya no podrás
                  cambiar tus notas.
                </p>
              </div>
              <Button
                className="shrink-0"
                disabled={processing}
                onClick={async () => {
                  // The winners in the confirmation are the saved grades: save first.
                  setProcessing(true);
                  await flushAll();
                  setProcessing(false);
                  setConfirming(true);
                }}
              >
                {processing && !confirming ? 'Guardando…' : 'Finalizar'}
              </Button>
            </div>
          )}
        </div>
        <div className="pb-12">
          <PlatanusLogo />
        </div>
      </div>

      <ProjectSheet project={detail} onClose={() => setDetailId(null)} />

      <AlertDialog open={confirming} onOpenChange={setConfirming}>
        <AlertDialogContent className="theme-ph26 max-h-[90vh] overflow-y-auto font-mono">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Finalizar tu evaluación?</AlertDialogTitle>
            <AlertDialogDescription>
              Estos son tus ganadores según tus notas. Una vez que finalices, tus
              notas y notas internas quedan cerradas.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <WinnersList results={results} criteria={criteria} />
          <AlertDialogFooter>
            <AlertDialogCancel disabled={processing}>Volver</AlertDialogCancel>
            <AlertDialogAction onClick={finalize} disabled={processing}>
              {processing ? 'Finalizando...' : 'Sí, finalizar'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

function Toggle<T extends string>({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: T;
  onChange: (value: T) => void;
  options: { value: T; label: string }[];
}) {
  return (
    <div className="flex items-center gap-2 text-sm">
      <span className="text-primary/70">{label}:</span>
      <div className="flex border-2 border-primary">
        {options.map((option) => (
          <button
            key={option.value}
            type="button"
            aria-pressed={value === option.value}
            onClick={() => onChange(option.value)}
            className={cn(
              'px-3 py-1 font-bold transition-colors',
              value === option.value
                ? 'bg-primary text-background'
                : 'text-primary hover:bg-primary/10',
            )}
          >
            {option.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function leaderNames(
  leaders: JudgeBallotProject[],
  criteria: JudgesStagesFinal['ballot']['criteria'],
): ReactNode {
  if (leaders.length === 0) return <span className="text-primary/50">—</span>;
  const score = weightedScore(leaders[0].scores, criteria);
  return (
    <>
      <span className="font-bold">
        {leaders.map((p) => p.name).join(' / ')}
      </span>
      {leaders.length > 1 && <span className="text-primary/60"> (empate)</span>}
      {score !== null && (
        <span className="ml-2 bg-primary px-1.5 font-bold text-background">
          {score.toFixed(2)}
        </span>
      )}
    </>
  );
}

// Who is winning, at the top of the final check.
function Podium({
  results,
  criteria,
}: {
  results: Standings;
  criteria: JudgesStagesFinal['ballot']['criteria'];
}) {
  return (
    <div className="flex flex-col gap-3 border-2 border-primary bg-primary/10 p-4 sm:p-5">
      <p className="flex items-center gap-2 font-bold text-primary text-sm uppercase">
        <Crown className="h-4 w-4" />
        Ganador overall
      </p>
      <p className="text-lg text-primary">
        {leaderNames(results.overall, criteria)}
      </p>
      <div className="grid gap-2 border-primary/30 border-t pt-3 sm:grid-cols-2">
        {results.byTrack.map(([track, leaders]) => (
          <p key={track} className="text-primary text-sm">
            <span className="flex items-center gap-1 text-primary/70 text-xs uppercase">
              <Trophy className="h-3 w-3" />
              {track}
            </span>
            {leaderNames(leaders, criteria)}
          </p>
        ))}
      </div>
    </div>
  );
}

function WinnersList({
  results,
  criteria,
}: {
  results: Standings;
  criteria: JudgesStagesFinal['ballot']['criteria'];
}) {
  return (
    <ul className="flex flex-col gap-2 text-primary text-sm">
      <li className="border-2 border-primary p-2">
        <span className="flex items-center gap-1 text-primary/70 text-xs uppercase">
          <Crown className="h-3 w-3" />
          Overall
        </span>
        {leaderNames(results.overall, criteria)}
      </li>
      {results.byTrack.map(([track, leaders]) => (
        <li key={track} className="border border-primary/40 p-2">
          <span className="flex items-center gap-1 text-primary/70 text-xs uppercase">
            <Trophy className="h-3 w-3" />
            {track}
          </span>
          {leaderNames(leaders, criteria)}
        </li>
      ))}
    </ul>
  );
}
